import React, {useEffect, useState} from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {Section} from 'constants/NavConstants';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {
  ItemListingSchemas,
  ItemListingSchemasByType,
  SchemasFilterFunction,
} from 'nav/sidepanel/listing/ItemListingUtil';
import {getStore} from 'redux-stores/Store';
import {TypesFilterSchemasQuery} from 'search/filters/__generated/TypesFilterSchemasQuery';
import {getEmptyFilterErrors} from 'search/filters/ValidateFilters';
import {
  Filter,
  FilterKey,
  keyToEmptyFilter,
  Operator,
  SearchParams,
  SearchType,
  SortKey,
} from 'search/SearchConstants';
import {getAppliedFiltersFromUserPreference} from 'search/SearchPreferences';
import {updateParamsAndSearch} from 'search/SearchUIReduxActions';
import withSearching, {SearchProps} from 'search/withSearching';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';

type TypesFilterTagSchema = TypesFilterSchemasQuery['tagSchemas'][number];
type ItemListingTagSchema = ItemListingSchemas['tagSchemas'][number];

function getAvailableSchemasFilter(schemaIds: Array<string>): SchemasFilterFunction {
  return (schemasByAvailableType: ItemListingSchemasByType): ItemListingSchemasByType => {
    const filteredSchemas: ItemListingSchemasByType = {};
    for (const type of Object.keys(schemasByAvailableType)) {
      const tagSchemas = schemasByAvailableType[type] as Array<ItemListingTagSchema | TypesFilterTagSchema>;
      const componentSchemas = tagSchemas.filter((schema) => schemaIds.includes(schema.id));
      filteredSchemas[type] = componentSchemas;
    }

    return filteredSchemas;
  };
}

function defaultSearchParams(schemaIds: Array<string>, componentTypes: Array<string>): SearchParams {
  const filters: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: componentTypes,
    },
    {
      ...keyToEmptyFilter(FilterKey.SCHEMA),
      operator: Operator.IS_ONE_OF,
      value: [...schemaIds],
    },
    ...getAppliedFiltersFromUserPreference(Section.COMPLEX_POLYMERS),
  ];

  return {
    filters,
    offset: 0,
    limit: 25,
    query: '',
    sorts: [],
  };
}

export function initializeSearchParams(schemaIds: Array<string>, componentTypes: Array<string>): void {
  const store = getStore();

  const searchParams = defaultSearchParams(schemaIds, componentTypes);

  store.dispatch(
    updateParamsAndSearch(
      Section.COMPLEX_POLYMERS,
      SearchType.FOLDERS_AND_ITEMS,
      searchParams,
      getEmptyFilterErrors(searchParams.filters)
    )
  );
}

export interface ComplexPolymerComponentSearchViewProps {
  schemaIds: Array<string>;
  componentTypes: Array<string>;
  rowSelectionMode: RowSelectionMode;
  setSelectedIds: (selectedIds: Array<string>) => void;
}

function ComplexPolymerComponentSearchViewListing(
  props: ComplexPolymerComponentSearchViewProps & SearchProps
): JSX.Element {
  const [isCompactDisplay, setIsCompactDisplay] = useState<boolean>(true);

  const onSelectIds = (selectedIds: Array<string>): void => {
    props.onSelectIds(selectedIds);
    props.setSelectedIds(selectedIds);
  };

  const availableSchemasFilter = getAvailableSchemasFilter(props.schemaIds);

  return (
    <ItemListing
      {...props}
      isTypeRequired={true}
      isCompactDisplay={isCompactDisplay}
      setIsCompactDisplay={setIsCompactDisplay}
      isExpanded={true}
      activeItemId={null}
      enableToggleExpanded={false}
      onSelectIds={onSelectIds}
      actionSource={ActionOrigin.MODBIO_COMPLEX_POLYMERS}
      availableTypes={props.componentTypes}
      makeCrumbs={undefined}
      extraPrimaryFilterOptions={[{filterKey: FilterKey.ANCESTOR_FOLDER}]}
      availableSortKeys={[
        SortKey.SORT_NAME_KEY,
        SortKey.SORT_IDENTIFIER_KEY,
        SortKey.SORT_MODIFIED_AT_KEY,
        SortKey.SORT_CREATED_AT_KEY,
      ]}
      renderToolbar={() => <></>}
      preservedFilterKeys={[FilterKey.TYPES, FilterKey.SCHEMA]}
      section={Section.COMPLEX_POLYMERS}
      disableOpenItem={true}
      disableBLAST={true}
      rowSelectionMode={props.rowSelectionMode}
      alwaysShowSelector={true}
      filterSchemas={availableSchemasFilter}
    />
  );
}

export default function ComplexPolymerComponentSearchView(
  props: ComplexPolymerComponentSearchViewProps
): JSX.Element {
  useEffect(() => {
    initializeSearchParams(props.schemaIds, props.componentTypes);
  }, [props.schemaIds, props.componentTypes]);

  const SearchingComponent = withSearching(
    ComplexPolymerComponentSearchViewListing,
    SearchType.FOLDERS_AND_ITEMS,
    Section.COMPLEX_POLYMERS,
    {}
  );
  return <SearchingComponent {...props} />;
}
